/**
 * MobileAddRecipe — Two-mode recipe creation screen.
 *
 * ┌ "Add Recipe Link" ─ just paste video/website links + speciality
 * │  + title + tags + notes → submit as a link_only recipe bookmark.
 * └ "Add Full Recipe" ── full workflow: type + speciality + name +
 *    description + serving/portion + prep/cook time + ingredients
 *    (add rows) + instructions (add rows) + images + video/website
 *    URLs + tags + notes → submit.
 *
 * Uses the existing `POST /api/recipes` endpoint. The category and
 * cuisine dropdowns render as bottom-sheet pickers to match the
 * mobile mockup exactly.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Package, Camera, Home as HomeIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#A9D64C";
const FP_GREEN_DARK = "#8AC136";

// Ordered exactly as the mobile mockup shows them (mobile users
// expect the taxonomy in this order; if a picked value isn't in
// RECIPE_CATEGORIES/CUISINES the API will 400, so we intersect with
// the config returned by /api/recipes/config.)
const RECIPE_TYPES = [
  "Breakfast", "Lunch", "Dinner", "Snack / Nasta", "Dessert / Sweets",
  "Beverage / Drink", "Appetizer / Starter", "Main Dish", "Side Dishes",
  "Soups", "Salads", "Smoothie", "Baby Food", "Meal Prep / Make-Ahead",
  "Quick Meals (10-20 min)", "Slow Cooker / Pressure Cooker", "Air Fryer",
  "Grilling / BBQ", "Baking", "Other",
];

const RECIPE_SPECIALITIES = [
  "African (North, West, East, Southern)",
  "Asian (South, East, Southeast, Central)",
  "European (Mediterranean, Nordic, Eastern)",
  "Middle Eastern", "Latin American", "North American", "Oceanic",
  "Chinese", "Indian", "Thai", "Japanese", "Italian", "Mexican",
  "Ethiopian", "Peruvian", "Lebanese", "Korean", "Other",
];

/* ---------- Sub-components ---------- */

function ScreenHeader({ title }) {
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3"
      style={{ background: "linear-gradient(180deg,#A9D64C 0%,#B5DA5E 100%)" }}
    >
      <button
        onClick={() => nav(-1)}
        data-testid="add-recipe-back"
        aria-label="Back"
        className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: FP_PURPLE }}>
        <span style={{ color: FP_GREEN, fontFamily: "serif", fontWeight: 700 }}>FP</span>
      </div>
      <h1 className="text-xl font-bold">{title}</h1>
    </header>
  );
}

function BottomSheet({ open, title, options, onSelect, onClose, testIdPrefix }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-end" data-testid={`${testIdPrefix}-sheet`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-h-[80vh] bg-white rounded-t-3xl overflow-y-auto pb-4 shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 bg-white border-b">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full border flex items-center justify-center" data-testid={`${testIdPrefix}-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul>
          {options.map((opt) => (
            <li key={opt}>
              <button
                onClick={() => { onSelect(opt); onClose(); }}
                data-testid={`${testIdPrefix}-opt-${opt.split(" ")[0].toLowerCase()}`}
                className="w-full px-5 py-4 text-left text-base border-b hover:bg-gray-50"
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <label className="block text-base font-bold mt-4 mb-2">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function OpenerField({ value, placeholder, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 border border-gray-300 rounded-xl px-4 py-3.5 text-left bg-white"
    >
      <Package className="w-4 h-4 opacity-40" />
      <span className={`flex-1 text-base ${value ? "text-gray-900" : "text-gray-400"}`}>{value || placeholder}</span>
      <span className="opacity-40">▾</span>
    </button>
  );
}

function TextField({ value, onChange, placeholder, testId, icon = true, ...rest }) {
  return (
    <div className="w-full flex items-center gap-3 border border-gray-300 rounded-xl px-4 py-3.5 bg-white">
      {icon && <Package className="w-4 h-4 opacity-40" />}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="flex-1 outline-none text-base bg-transparent placeholder-gray-400"
        {...rest}
      />
    </div>
  );
}

function TextArea({ value, onChange, placeholder, testId, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testId}
      className="w-full border border-gray-300 rounded-xl px-4 py-3.5 bg-white outline-none text-base placeholder-gray-400"
    />
  );
}

function GreenButton({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full mt-3 rounded-xl py-3.5 text-white text-base font-semibold"
      style={{ background: FP_GREEN_DARK }}
    >
      {children}
    </button>
  );
}

function PurpleSubmit({ onClick, disabled, testId, children = "Submit" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="w-full py-4 rounded-xl text-white text-lg font-semibold shadow disabled:opacity-50"
      style={{ background: FP_PURPLE }}
    >
      {children}
    </button>
  );
}

/* ---------- Main screen ---------- */

export default function MobileAddRecipe() {
  const { user } = useAuth() || {};
  const nav = useNavigate();

  // Mode toggle
  const [mode, setMode] = useState("full"); // 'link' | 'full'

  // Shared fields
  const [recipeType, setRecipeType] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [title, setTitle] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [notes, setNotes] = useState("");

  // Full-recipe fields
  const [shortDesc, setShortDesc] = useState("");
  const [servingSize, setServingSize] = useState("");
  const [portionSize, setPortionSize] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [cookTime, setCookTime] = useState("");
  const [ingName, setIngName] = useState("");
  const [ingQty, setIngQty] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [instText, setInstText] = useState("");
  const [instructions, setInstructions] = useState([]);

  // Links + images (both modes)
  const [videoUrl, setVideoUrl] = useState("");
  const [videoUrls, setVideoUrls] = useState([]);
  const [siteUrl, setSiteUrl] = useState("");
  const [siteUrls, setSiteUrls] = useState([]);
  const [images, setImages] = useState([]);

  // Bottom-sheet visibility
  const [sheet, setSheet] = useState(null); // 'type' | 'speciality' | null

  const [submitting, setSubmitting] = useState(false);

  const addIngredient = () => {
    if (!ingName.trim()) return;
    setIngredients((prev) => [...prev, { name: ingName.trim(), quantity: ingQty.trim() }]);
    setIngName(""); setIngQty("");
  };
  const removeIngredient = (i) => setIngredients((prev) => prev.filter((_, idx) => idx !== i));

  const addInstruction = () => {
    if (!instText.trim()) return;
    setInstructions((prev) => [...prev, instText.trim()]);
    setInstText("");
  };
  const removeInstruction = (i) => setInstructions((prev) => prev.filter((_, idx) => idx !== i));

  const addVideoUrl = () => {
    if (!videoUrl.trim()) return;
    setVideoUrls((prev) => [...prev, videoUrl.trim()]);
    setVideoUrl("");
  };
  const addSiteUrl = () => {
    if (!siteUrl.trim()) return;
    setSiteUrls((prev) => [...prev, siteUrl.trim()]);
    setSiteUrl("");
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }))).then((urls) => setImages((prev) => [...prev, ...urls].slice(0, 8)));
  };
  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!user) { toast.error("Please log in first"); nav("/login?next=%2Fm%2Fadd-recipe"); return; }
    if (!title.trim() || title.trim().length < 3) { toast.error("Title is required (min 3 chars)"); return; }
    if (!recipeType) { toast.error("Please pick a Recipe Type"); return; }
    if (!speciality) { toast.error("Please pick a Recipe Speciality"); return; }
    if (mode === "full" && ingredients.length === 0) { toast.error("Add at least one ingredient"); return; }
    if (mode === "full" && instructions.length === 0) { toast.error("Add at least one instruction"); return; }
    if (mode === "link" && videoUrls.length === 0 && siteUrls.length === 0) {
      toast.error("Add at least one video or website link"); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        short_description: shortDesc.trim(),
        category: recipeType,
        cuisine: speciality,
        country: "United States",
        prep_time_min: parseInt(prepTime, 10) || 0,
        cook_time_min: parseInt(cookTime, 10) || 0,
        servings: parseInt(servingSize, 10) || 4,
        portion_size: portionSize || null,
        difficulty: "Beginner",
        ingredients: ingredients.map((i) => ({ name: i.name, quantity: i.quantity, unit: "" })),
        steps: instructions.map((t, n) => ({ n: n + 1, text: t })),
        gallery: images.map((data) => ({ data })),
        cover_photo: images[0] || null,
        video_urls: videoUrls,
        website_urls: siteUrls,
        tags: tagsRaw.split(",").map((s) => s.trim()).filter(Boolean),
        chef_notes: notes || null,
        additional_notes: notes || null,
        link_only: mode === "link",
      };
      const res = await axios.post(`${API}/api/recipes`, payload, {
        headers: { Authorization: `Bearer ${user.token || ""}` },
      });
      toast.success("Recipe submitted!");
      nav(`/m/recipes`);
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Submit failed";
      toast.error(typeof msg === "string" ? msg : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-10 bg-white" data-testid="mobile-add-recipe">
      <ScreenHeader title="Add Recipes" />

      {/* Mode toggle */}
      <div
        className="px-4 pt-2 pb-4 flex gap-3"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {[
          { key: "link", label: "Add Recipe Link" },
          { key: "full", label: "Add Full Recipe" },
        ].map((m) => {
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`recipe-mode-${m.key}`}
              className="flex-1 py-3.5 rounded-2xl text-base font-bold shadow-md"
              style={{
                background: active ? FP_PURPLE : "#fff",
                color: active ? "#fff" : "#1c1c1c",
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="px-4 pt-2 space-y-2">
        <h2 className="text-xl font-bold mt-2">
          {mode === "link" ? "Add Recipes Link" : "Add New Recipes"}
        </h2>

        <FieldLabel required>Recipe Type</FieldLabel>
        <OpenerField
          value={recipeType}
          placeholder="Select Recipe Type"
          onClick={() => setSheet("type")}
          testId="recipe-type-opener"
        />

        <FieldLabel required={mode === "full"}>Recipe Speciality</FieldLabel>
        <OpenerField
          value={speciality}
          placeholder="Select Recipe Speciality"
          onClick={() => setSheet("speciality")}
          testId="recipe-speciality-opener"
        />

        <FieldLabel required>Recipe Name/Title</FieldLabel>
        <TextField value={title} onChange={setTitle} placeholder="Recipe Name/Title" testId="recipe-title-input" />

        {mode === "full" && (
          <>
            <FieldLabel>Short Description</FieldLabel>
            <TextArea value={shortDesc} onChange={setShortDesc} placeholder="Short Description" testId="recipe-desc-input" />

            <FieldLabel>Serving Size</FieldLabel>
            <TextField value={servingSize} onChange={setServingSize} placeholder="Serving Size" testId="recipe-serving-input" type="number" inputMode="numeric" />

            <FieldLabel>Portion Size</FieldLabel>
            <TextField value={portionSize} onChange={setPortionSize} placeholder="Portion Size" testId="recipe-portion-input" />

            <FieldLabel>Prep Time</FieldLabel>
            <TextField value={prepTime} onChange={setPrepTime} placeholder="Prep Time (minutes)" testId="recipe-prep-input" type="number" inputMode="numeric" />

            <FieldLabel>Cook Time</FieldLabel>
            <TextField value={cookTime} onChange={setCookTime} placeholder="Cook Time (minutes)" testId="recipe-cook-input" type="number" inputMode="numeric" />

            {/* Ingredients */}
            <h2 className="text-lg font-bold mt-6">Add Ingredients</h2>
            <div className="flex gap-2 mt-2">
              <input
                value={ingName}
                onChange={(e) => setIngName(e.target.value)}
                placeholder="Ingredients Name"
                data-testid="ing-name-input"
                className="flex-1 border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base placeholder-gray-400"
              />
              <input
                value={ingQty}
                onChange={(e) => setIngQty(e.target.value)}
                placeholder="Qty/Unit"
                data-testid="ing-qty-input"
                className="w-28 border border-gray-300 rounded-xl px-3 py-3.5 outline-none text-base placeholder-gray-400"
              />
            </div>
            <GreenButton onClick={addIngredient} testId="add-ingredient-btn">Add Ingredient</GreenButton>
            {ingredients.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="ingredients-list">
                {ingredients.map((i, idx) => (
                  <li key={idx} className="flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2 text-sm">
                    <span className="flex-1"><b>{i.name}</b>{i.quantity ? ` — ${i.quantity}` : ""}</span>
                    <button onClick={() => removeIngredient(idx)} aria-label="Remove" data-testid={`ing-remove-${idx}`}>
                      <X className="w-4 h-4 opacity-60" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Instructions */}
            <h2 className="text-lg font-bold mt-6">Add Instruction</h2>
            <TextArea value={instText} onChange={setInstText} placeholder="Enter Instruction" testId="inst-input" rows={3} />
            <GreenButton onClick={addInstruction} testId="add-instruction-btn">Add Instruction</GreenButton>
            {instructions.length > 0 && (
              <ol className="mt-3 space-y-2 list-decimal list-inside" data-testid="instructions-list">
                {instructions.map((s, idx) => (
                  <li key={idx} className="flex items-start gap-2 bg-gray-50 border rounded-lg px-3 py-2 text-sm">
                    <span className="font-bold">{idx + 1}.</span>
                    <span className="flex-1">{s}</span>
                    <button onClick={() => removeInstruction(idx)} aria-label="Remove" data-testid={`inst-remove-${idx}`}>
                      <X className="w-4 h-4 opacity-60" />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}

        {/* Images */}
        <h2 className="text-lg font-bold mt-6">Add Recipes Images</h2>
        <label
          className="border border-dashed border-gray-400 rounded-xl w-full flex flex-col items-center justify-center py-8 cursor-pointer"
          data-testid="recipe-image-upload"
        >
          <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
          <span className="text-sm">Add Recipes Images</span>
          <Plus className="w-6 h-6 mt-2" />
        </label>
        {images.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="recipe-images-preview">
            {images.map((src, idx) => (
              <div key={idx} className="relative">
                <img src={src} alt={`upload-${idx}`} className="w-full h-20 object-cover rounded-lg" />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute top-0 right-0 w-6 h-6 bg-white/90 rounded-bl-lg flex items-center justify-center"
                  aria-label="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Video links */}
        <h2 className="text-lg font-bold mt-6">Add Multiple Video Links</h2>
        <TextField value={videoUrl} onChange={setVideoUrl} placeholder="www.youtube.com" testId="video-url-input" />
        <GreenButton onClick={addVideoUrl} testId="add-video-btn">Add Multiple Video Links</GreenButton>
        {videoUrls.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="video-urls-list">
            {videoUrls.map((u, idx) => (
              <li key={idx} className="flex items-center gap-2 text-xs bg-gray-50 border rounded-lg px-3 py-2">
                <span className="flex-1 truncate">{u}</span>
                <button onClick={() => setVideoUrls((p) => p.filter((_, i) => i !== idx))} aria-label="Remove">
                  <X className="w-4 h-4 opacity-60" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Website links */}
        <h2 className="text-lg font-bold mt-6">Add Multiple Website Links</h2>
        <TextField value={siteUrl} onChange={setSiteUrl} placeholder="www.recipes.com" testId="site-url-input" />
        <GreenButton onClick={addSiteUrl} testId="add-site-btn">Add Multiple Website Links</GreenButton>
        {siteUrls.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="site-urls-list">
            {siteUrls.map((u, idx) => (
              <li key={idx} className="flex items-center gap-2 text-xs bg-gray-50 border rounded-lg px-3 py-2">
                <span className="flex-1 truncate">{u}</span>
                <button onClick={() => setSiteUrls((p) => p.filter((_, i) => i !== idx))} aria-label="Remove">
                  <X className="w-4 h-4 opacity-60" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Tags */}
        <h2 className="text-lg font-bold mt-6">Add Recipe Tags</h2>
        <TextField value={tagsRaw} onChange={setTagsRaw} placeholder="Add Recipe Tags (comma-separated)" testId="tags-input" />

        {/* Notes */}
        <h2 className="text-lg font-bold mt-6">{mode === "link" ? "Add Additional Notes" : "Additional Notes"}</h2>
        <TextArea value={notes} onChange={setNotes} placeholder="Additional Notes" testId="notes-input" rows={4} />

        <div className="mt-6">
          <PurpleSubmit onClick={submit} disabled={submitting} testId="recipe-submit-btn">
            {submitting ? "Submitting…" : "Submit"}
          </PurpleSubmit>
        </div>
      </div>

      <BottomSheet
        open={sheet === "type"}
        title="Select Recipe Type"
        options={RECIPE_TYPES}
        onSelect={setRecipeType}
        onClose={() => setSheet(null)}
        testIdPrefix="type"
      />
      <BottomSheet
        open={sheet === "speciality"}
        title="Select Recipe Speciality"
        options={RECIPE_SPECIALITIES}
        onSelect={setSpeciality}
        onClose={() => setSheet(null)}
        testIdPrefix="speciality"
      />
    </div>
  );
}
